import React, { createContext, useContext, useState, useCallback } from "react";
import {
  IPaymentReminderConfigForm,
  initialPaymentReminderConfigForm,
} from "../types/paymentReminderConfig.types";
import { upsertPaymentReminderConfig } from "../services/paymentReminderConfig.service";
import { useDispatchWrapper } from "../../../../hooks";
import { showMessage } from "../../../../redux/messages/messagesActions";
import { useBillingCompanyContext } from "../../../../providers";

interface IPaymentReminderConfigContext {
  formState: IPaymentReminderConfigForm;
  activeStep: number;
  loading: boolean;
  mode: "page" | "dialog";
  setActiveStep: (step: number) => void;
  updateFormState: (partial: Partial<IPaymentReminderConfigForm>) => void;
  setFormState: (state: IPaymentReminderConfigForm) => void;
  goNext: () => void;
  goPrev: () => void;
  handleSubmit: (overrideData?: Partial<IPaymentReminderConfigForm>) => Promise<boolean>;
}

const PaymentReminderConfigContext = createContext<
  IPaymentReminderConfigContext | undefined
>(undefined);

interface PaymentReminderConfigProviderProps {
  children: React.ReactNode;
  mode: "page" | "dialog";
  initialData?: IPaymentReminderConfigForm | null;
  onSuccess?: () => void;
}

export const PaymentReminderConfigProvider: React.FC<
  PaymentReminderConfigProviderProps
> = ({ children, mode, initialData, onSuccess }) => {
  const dispatch = useDispatchWrapper();
  const { comapny } = useBillingCompanyContext();

  const [formState, setFormState] = useState<IPaymentReminderConfigForm>(
    initialData || {
      ...initialPaymentReminderConfigForm,
      company_outlet_uuid: comapny.company_outlet_uuid,
    },
  );
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const updateFormState = useCallback(
    (partial: Partial<IPaymentReminderConfigForm>) => {
      setFormState((prev) => ({ ...prev, ...partial }));
    },
    [],
  );

  const goNext = useCallback(() => {
    setActiveStep((prev) => Math.min(prev + 1, 2));
  }, []);

  const goPrev = useCallback(() => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  }, []);

  const handleSubmit = useCallback(async (overrideData?: Partial<IPaymentReminderConfigForm>): Promise<boolean> => {
    setLoading(true);
    try {
      const finalPayload = overrideData
        ? { ...formState, ...overrideData }
        : formState;
      await upsertPaymentReminderConfig(finalPayload);
      dispatch(
        showMessage({
          type: "success",
          message: "Payment reminder configuration saved successfully",
          displayAs: "snackbar",
        }),
      );
      onSuccess?.();
      return true;
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message ||
            "Failed to save payment reminder configuration",
          displayAs: "snackbar",
        }),
      );
      return false;
    } finally {
      setLoading(false);
    }
  }, [formState, dispatch, onSuccess]);

  return (
    <PaymentReminderConfigContext.Provider
      value={{
        formState,
        activeStep,
        loading,
        mode,
        setActiveStep,
        updateFormState,
        setFormState,
        goNext,
        goPrev,
        handleSubmit,
      }}
    >
      {children}
    </PaymentReminderConfigContext.Provider>
  );
};

export const usePaymentReminderConfig = () => {
  const context = useContext(PaymentReminderConfigContext);
  if (!context) {
    throw new Error(
      "usePaymentReminderConfig must be used within a PaymentReminderConfigProvider",
    );
  }
  return context;
};
